import { motion } from 'framer-motion'
import { Link } from 'react-router-dom'
import { Heart, Lightbulb, TrendingUp, Users, Target, Zap, ArrowRight } from 'lucide-react'

export default function About(){
  const values = [
    {
      icon: Heart,
      title: 'Growth-Obsessed',
      description: 'We measure success by your growth. Every campaign, every strategy, every decision is driven by delivering real, measurable results that move your business forward.',
      color: 'carrot'
    },
    {
      icon: Lightbulb,
      title: 'Innovation First',
      description: 'The digital landscape never stops evolving, and neither do we. We stay ahead of trends, test new strategies, and find creative solutions that give you a competitive edge.',
      color: 'leaf'
    },
    {
      icon: Users,
      title: 'Partnership Mindset',
      description: 'Your success is our success. We\'re not just a vendor—we\'re an extension of your team, invested in your goals and committed to long-term growth together.',
      color: 'carrot'
    },
    {
      icon: Target,
      title: 'Data-Driven Decisions',
      description: 'No guesswork, no gut feelings. Every recommendation is backed by data, every optimization is tested, and every strategy is designed to maximize your ROI.',
      color: 'leaf'
    }
  ]

  const team = [
    {
      role: 'Strategy & Analytics',
      description: 'Deep expertise in performance marketing, conversion optimization, and growth strategy. We turn data into actionable insights that drive results.'
    },
    {
      role: 'Creative & Content',
      description: 'Compelling storytelling meets conversion-focused design. We create campaigns that resonate with your audience and drive action.'
    },
    {
      role: 'Technical Excellence',
      description: 'From tracking implementation to funnel optimization, we ensure every technical detail is dialed in for maximum performance.'
    }
  ]

  return (
    <>
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-gradient-to-br from-white via-cloud-50/30 to-white">
        <div className="container">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-4xl mx-auto text-center"
          >
            <div className="inline-block mb-6 px-5 py-2.5 bg-gradient-to-r from-carrot-50 to-carrot-100/50 text-carrot-700 rounded-full text-sm font-semibold tracking-wide shadow-subtle">
              About AdKarrot
            </div>
            <h1 className="text-6xl md:text-7xl font-bold text-ink-900 mb-8 leading-[1.1]">
              Smart Ads, Real Results That Actually Work
            </h1>
            <p className="text-xl md:text-2xl text-ink-600 font-light leading-relaxed mb-12">
              We're on a mission to help small businesses thrive in the digital age with performance marketing
              that delivers real, measurable results.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Mission Statement */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="bg-gradient-to-br from-carrot-50 to-white rounded-[3rem] p-12 md:p-16 border border-carrot-100 shadow-soft"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-ink-900 mb-6">Our Story</h2>
              <div className="space-y-6 text-lg text-ink-700 leading-relaxed">
                <p>
                  AdKarrot was born from a simple realization: small businesses deserve the same level of marketing
                  expertise that big corporations have, without the enterprise price tag or complicated jargon.
                </p>
                <p>
                  We've seen too many talented entrepreneurs struggle with marketing that doesn't deliver. Agencies
                  that promise the world but deliver vanity metrics. Consultants who talk strategy but never execute.
                  Platforms that are "easy to use" but impossible to master.
                </p>
                <p className="font-semibold text-carrot-700">
                  We built AdKarrot to be different.
                </p>
                <p>
                  Every strategy we create is grounded in data. Every campaign we run is optimized for ROI.
                  Every recommendation we make is designed to move your business forward—not just to sound impressive
                  in a meeting.
                </p>
                <p>
                  Today, we're proud to help Iowa small businesses grow revenue, reduce wasted ad spend, and build
                  marketing systems that compound results over time. And we're just getting started.
                </p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Core Values */}
      <section className="py-20 bg-gradient-to-br from-cloud-50/50 to-white">
        <div className="container">
          <div className="max-w-3xl mx-auto text-center mb-16">
            <motion.h2
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="text-4xl md:text-5xl font-bold text-ink-900 mb-6"
            >
              What Drives Us
            </motion.h2>
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
              className="text-xl text-ink-600 font-light"
            >
              Our values aren't just words on a wall—they're the principles that guide every decision we make.
            </motion.p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {values.map((value, idx) => {
              const Icon = value.icon
              return (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: idx * 0.1, ease: [0.16, 1, 0.3, 1] }}
                  className="bg-white rounded-3xl border border-ink-100 p-8 shadow-card hover:shadow-soft transition-all duration-500 ease-apple"
                >
                  <div className={`w-16 h-16 rounded-2xl bg-${value.color}-100 flex items-center justify-center mb-6`}>
                    <Icon className={`w-8 h-8 text-${value.color}-600`} />
                  </div>
                  <h3 className="text-2xl font-bold text-ink-900 mb-4">{value.title}</h3>
                  <p className="text-ink-600 leading-relaxed">{value.description}</p>
                </motion.div>
              )
            })}
          </div>
        </div>
      </section>

      {/* Team & Expertise */}
      <section className="py-20 bg-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="text-center mb-16"
            >
              <h2 className="text-4xl md:text-5xl font-bold text-ink-900 mb-6">
                Expertise That Delivers
              </h2>
              <p className="text-xl text-ink-600 font-light">
                A lean, experienced team focused on what matters: your growth.
              </p>
            </motion.div>

            <div className="space-y-6">
              {team.map((member, idx) => (
                <motion.div
                  key={idx}
                  initial={{ opacity: 0, x: -20 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: idx * 0.1, ease: [0.16, 1, 0.3, 1] }}
                  className="bg-gradient-to-r from-cloud-50 to-white rounded-2xl p-8 border border-ink-100"
                >
                  <h3 className="text-xl font-bold text-ink-900 mb-3">{member.role}</h3>
                  <p className="text-ink-600 leading-relaxed">{member.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-gradient-to-br from-cloud-50/50 to-white">
        <div className="container">
          <div className="max-w-4xl mx-auto">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <h2 className="text-4xl md:text-5xl font-bold text-ink-900 mb-8 text-center">
                Why Work With AdKarrot?
              </h2>

              <div className="space-y-8">
                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <Zap className="w-8 h-8 text-carrot-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-ink-900 mb-2">No Fluff, Just Results</h3>
                    <p className="text-ink-600 leading-relaxed">
                      We skip the marketing jargon and focus on what matters: revenue growth, reduced costs,
                      and sustainable systems that work long-term.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <TrendingUp className="w-8 h-8 text-leaf-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-ink-900 mb-2">Transparent & Accountable</h3>
                    <p className="text-ink-600 leading-relaxed">
                      You'll always know exactly what we're doing, why we're doing it, and what results we're
                      driving. No smoke and mirrors, no vanity metrics.
                    </p>
                  </div>
                </div>

                <div className="flex gap-4">
                  <div className="flex-shrink-0">
                    <Target className="w-8 h-8 text-carrot-600" />
                  </div>
                  <div>
                    <h3 className="text-xl font-bold text-ink-900 mb-2">Tailored to Your Business</h3>
                    <p className="text-ink-600 leading-relaxed">
                      We don't believe in cookie-cutter strategies. Every recommendation is customized to your
                      unique business, audience, and goals.
                    </p>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-gradient-to-br from-ink-900 via-ink-800 to-ink-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-carrot-500 rounded-full blur-3xl" />
          <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-leaf-500 rounded-full blur-3xl" />
        </div>

        <div className="container relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            >
              <h2 className="text-5xl md:text-6xl font-bold mb-8 leading-tight">
                Ready to grow together?
              </h2>
              <p className="text-xl md:text-2xl text-cloud-200 mb-12 leading-relaxed font-light max-w-3xl mx-auto">
                Let's talk about your business goals and create a custom marketing strategy that actually delivers results.
              </p>
              <Link
                to="/pricing"
                className="group inline-flex items-center px-10 py-5 bg-carrot-500 hover:bg-carrot-600 text-white font-semibold text-lg rounded-2xl shadow-glow hover:shadow-card-hover transition-all duration-300 ease-apple transform hover:scale-[1.02]"
              >
                Book Your Strategy Call
                <ArrowRight className="ml-3 w-6 h-6 group-hover:translate-x-0.5 transition-transform duration-300" />
              </Link>
            </motion.div>
          </div>
        </div>
      </section>
    </>
  )
}
